import type {
  AiOperationTargetRef,
  AiOperationalCollaborationSignal,
  AiOperationalContinuitySummary,
  AiOperationalContinuityThread,
  AiOperationalRecommendation,
  AiOperationalRequest,
  AiOperationalSurface,
} from '../../../types/aiOperational'
import type { AiOperationalContextWindow } from '../context/operationalRetrieval'

const nowIso = () => new Date().toISOString()

const stableId = (prefix: string, parts: string[]) =>
  `${prefix}_${parts.join('_').replace(/[^0-9A-Za-z_]/g, '_').slice(0, 72) || 'workspace'}`

const targetFromRecord = (record: AiOperationalContextWindow['primary'][number]): AiOperationTargetRef => ({
  id: record.id,
  type: record.type,
  title: record.title,
  board: record.board,
  parentId: record.parentId,
})

const uniqueTargets = (targets: AiOperationTargetRef[]) => {
  const seen = new Set<string>()
  return targets.filter((target) => {
    const key = `${target.type}:${target.id}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  }).slice(0, 8)
}

const relatedSystemsForText = (text: string): AiOperationalContinuityThread['relatedSystems'] => {
  const lower = text.toLowerCase()
  const systems: AiOperationalContinuityThread['relatedSystems'] = ['AI']
  if (/\b(checklist|dependency|proof|location|complete|status)\b/.test(lower)) systems.push('CHECKLISTS')
  if (/\b(meeting|agenda|huddle|standup|rsvp)\b/.test(lower)) systems.push('MEETINGS')
  if (/\b(upload|file|proof|drive|artifact)\b/.test(lower)) systems.push('UPLOADS')
  if (/\b(automation|workflow|trigger|webhook)\b/.test(lower)) systems.push('AUTOMATIONS')
  if (/\b(report|summary|ledger|health)\b/.test(lower)) systems.push('REPORTS')
  if (/\b(review|approval|audit|rollback|permission|governance)\b/.test(lower)) systems.push('GOVERNANCE')
  if (/\b(sync|integration|notion|jira|calendar|provider)\b/.test(lower)) systems.push('INTEGRATIONS')
  if (/\b(mobile|helper|member|offline|sync)\b/.test(lower)) systems.push('MOBILE')
  return [...new Set(systems)]
}

const recordText = (record: AiOperationalContextWindow['primary'][number]) =>
  `${record.title} ${record.status ?? ''} ${(record.reasons ?? []).join(' ')}`

const recommendationFromForecast = (
  forecast: NonNullable<AiOperationalContextWindow['digest']['forecasts']>[number],
  targets: AiOperationTargetRef[],
): AiOperationalRecommendation => ({
  id: stableId('rec_forecast', [forecast.kind, forecast.id]),
  kind: forecast.kind === 'WORKLOAD_CONCENTRATION' ? 'SEQUENCE_WORK' : forecast.kind === 'READINESS_BLOCKER' ? 'READINESS_FIX' : 'NEXT_ACTION',
  priority: forecast.severity === 'CRITICAL' ? 'HIGH' : forecast.severity === 'WATCH' ? 'MEDIUM' : 'LOW',
  title: forecast.kind.replace(/_/g, ' ').toLowerCase(),
  rationale: forecast.summary,
  targetRefs: targets,
  source: 'FORECAST',
  suggestedActionLabel: forecast.recommendedAction,
  reviewSafe: true,
  generatedAtIso: forecast.generatedAtIso,
})

const buildReviewRecommendation = (
  request: AiOperationalRequest,
  targets: AiOperationTargetRef[],
): AiOperationalRecommendation | null => {
  if (!/\b(create|add|change|update|edit|modify|set|rename|move|complete|mark|link|unlink|dependency)\b/i.test(request.prompt)) {
    return null
  }
  return {
    id: stableId('rec_review', [request.surface, request.mode, ...targets.map((target) => target.id)]),
    kind: 'REVIEW_PROPOSAL',
    priority: 'HIGH',
    title: 'prepare reviewable operation',
    rationale: 'The request appears to affect persistent workspace state, so the next step should stay inside the proposal/review boundary.',
    targetRefs: targets,
    source: 'EXECUTION_GRAPH',
    suggestedActionLabel: 'Prepare proposal for review',
    reviewSafe: true,
    generatedAtIso: nowIso(),
  }
}

const buildCollaborationSignals = (
  context: AiOperationalContextWindow,
  targets: AiOperationTargetRef[],
): AiOperationalCollaborationSignal[] => {
  const signals: AiOperationalCollaborationSignal[] = []
  const primaryText = context.primary.map(recordText).join(' ')
  const dependencyTargets = context.primary.filter((record) =>
    record.type === 'CHECKLIST' && /\b(block|dependency|prerequisite|proof|location|resource)\b/i.test(recordText(record)))
  if (dependencyTargets.length) {
    signals.push({
      id: stableId('collab_dependency', dependencyTargets.map((record) => record.id)),
      kind: 'DEPENDENCY_CONFLICT',
      severity: 'WATCH',
      summary: 'Dependency or readiness gates may affect the proposed operation.',
      targetRefs: uniqueTargets(dependencyTargets.map(targetFromRecord)),
      audience: 'REVIEWERS',
      explanation: 'Reviewers should verify proof, location, resource, and prerequisite state before approval.',
      generatedAtIso: nowIso(),
    })
  }
  if (/\b(proposal|review|approval|pending)\b/i.test(primaryText)) {
    signals.push({
      id: stableId('collab_proposal', targets.map((target) => target.id)),
      kind: 'PROPOSAL_COLLISION',
      severity: 'INFO',
      summary: 'Related proposal or review language was found in the bounded context.',
      targetRefs: targets,
      audience: 'REVIEWERS',
      explanation: 'Line should surface existing review context before creating another operational path.',
      generatedAtIso: nowIso(),
    })
  }
  if (context.digest.forecasts?.some((forecast) => forecast.kind === 'TIMELINE_DRIFT')) {
    signals.push({
      id: stableId('collab_stale', targets.map((target) => target.id)),
      kind: 'STALE_ASSUMPTION',
      severity: 'WATCH',
      summary: 'Timeline drift means current assumptions may be stale.',
      targetRefs: targets,
      audience: 'REVIEWERS',
      explanation: 'A reviewer should confirm dates, owners, and blockers before executing changes.',
      generatedAtIso: nowIso(),
    })
  }
  return signals.slice(0, 6)
}

const buildContinuityThreads = (
  request: AiOperationalRequest,
  context: AiOperationalContextWindow,
  targets: AiOperationTargetRef[],
): AiOperationalContinuityThread[] => {
  const threadTargets = targets.length ? targets : uniqueTargets(context.primary.slice(0, 4).map(targetFromRecord))
  const systems = relatedSystemsForText([
    request.prompt,
    context.digest.summary,
    ...(context.digest.forecasts ?? []).map((forecast) => forecast.summary),
  ].join(' '))
  const surface: AiOperationalSurface = request.surface
  return [{
    id: stableId('thread', [surface, ...threadTargets.map((target) => target.id)]),
    label: 'Workspace operation',
    summary: 'Connect this request to the current workspace context, review boundary, and affected operational systems.',
    surface,
    targetRefs: threadTargets,
    relatedSystems: systems,
  }]
}

export const buildOperationalContinuity = ({
  request,
  context,
}: {
  request: AiOperationalRequest
  context: AiOperationalContextWindow
}): AiOperationalContinuitySummary => {
  const targets = uniqueTargets([
    ...(request.selectedTargets ?? []),
    ...context.primary.slice(0, 5).map(targetFromRecord),
  ])
  const forecastRecommendations = (context.digest.forecasts ?? [])
    .slice(0, 4)
    .map((forecast) => recommendationFromForecast(forecast, targets))
  const reviewRecommendation = buildReviewRecommendation(request, targets)
  const recommendations = [
    ...(reviewRecommendation ? [reviewRecommendation] : []),
    ...forecastRecommendations,
  ].slice(0, 6)

  return {
    recommendations,
    collaborationSignals: buildCollaborationSignals(context, targets),
    threads: buildContinuityThreads(request, context, targets),
  }
}
